import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Shield, Play, RefreshCw, Globe, Server, Smartphone, Cpu, Container,
  Wifi, Lock, CheckCircle, Clock, Loader2, Plus, FileType, Upload,
  Crosshair, Radar, Eye, Zap, ShieldCheck, Search as SearchIcon,
  ArrowRight, CircleDot, FileBarChart, Activity, Bug, Coins
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scanTypeIcons = { web: Globe, api: Lock, network: Wifi, infrastructure: Server, container: Container, kubernetes: Cpu, mobile: Smartphone, iot: Wifi };

const scanModes = [
  { value: 'standard', label: 'Standard', icon: Shield, desc: 'Balanced scan with common vulnerability checks' },
  { value: 'spider', label: 'Spider / Crawl', icon: Radar, desc: 'Discover hidden endpoints before scanning' },
  { value: 'aggressive', label: 'Aggressive', icon: Zap, desc: 'Deep scan with exploit confirmation' },
  { value: 'stealth', label: 'Stealth', icon: Eye, desc: 'Low-footprint passive scan' },
];

const pipelineSteps = [
  { step: 1, name: 'Crawl Application', icon: SearchIcon, desc: 'Map endpoints, forms, and parameters' },
  { step: 2, name: 'Passive Scan', icon: Eye, desc: 'Analyze responses for information leaks' },
  { step: 3, name: 'Active Scan', icon: Zap, desc: 'Send payloads to test for vulnerabilities' },
  { step: 4, name: 'Analyze Results', icon: Activity, desc: 'Correlate and deduplicate findings' },
  { step: 5, name: 'Validate Findings', icon: ShieldCheck, desc: 'Confirm exploits, remove false positives' },
  { step: 6, name: 'Generate Report', icon: FileBarChart, desc: 'Create detailed vulnerability report' },
];

export default function ScannerPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [costs, setCosts] = useState({ scan: 10 });
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [scans, setScans] = useState([]);
  const [scanTypes, setScanTypes] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(searchParams.get('product_id') || '');
  const [selectedEngagement, setSelectedEngagement] = useState(searchParams.get('engagement_id') || '');
  const [selectedScanType, setSelectedScanType] = useState('web');
  const [scanMode, setScanMode] = useState('standard');
  const [target, setTarget] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', asset_type: 'web', environment: 'production' });
  const [enableSpider, setEnableSpider] = useState(false);
  const [confirmFindings, setConfirmFindings] = useState(false);
  const [maxDepth, setMaxDepth] = useState(3);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => { fetchProjects(); fetchScans(); fetchScanTypes(); fetchProducts(); axios.get(`${API}/credits/info`, { withCredentials: true }).then(r => setCosts(r.data.costs)).catch(() => {}); }, []);
  useEffect(() => {
    if (selectedProduct && selectedProduct !== 'none') {
      fetchEngagements(selectedProduct);
    } else { setEngagements([]); setSelectedEngagement(''); }
  }, [selectedProduct]);

  const fetchProjects = async () => { try { const r = await axios.get(`${API}/projects`, { withCredentials: true }); setProjects(r.data); if (r.data.length > 0 && !selectedProject) setSelectedProject(r.data[0].project_id); } catch {} };
  const fetchScans = async () => { try { const r = await axios.get(`${API}/scans`, { withCredentials: true }); setScans(r.data); } catch {} };
  const fetchScanTypes = async () => { try { const r = await axios.get(`${API}/scan-types`, { withCredentials: true }); setScanTypes(r.data); } catch {} };
  const fetchProducts = async () => { try { const r = await axios.get(`${API}/products`, { withCredentials: true }); setProducts(r.data); } catch {} };
  const fetchEngagements = async (pid) => { try { const r = await axios.get(`${API}/products/${pid}/engagements`, { withCredentials: true }); setEngagements(r.data); } catch {} };

  const createProject = async () => {
    try {
      const r = await axios.post(`${API}/projects`, newProject, { withCredentials: true });
      setProjects(prev => [r.data, ...prev]);
      setSelectedProject(r.data.project_id);
      setShowNewProject(false);
      setNewProject({ name: '', description: '', asset_type: 'web', environment: 'production' });
      toast.success('Project created');
    } catch {}
  };

  const startScan = async () => {
    if (!selectedProject) return;
    setScanning(true);
    setScanResult(null);
    setCurrentStep(1);

    // Simulate pipeline steps visually
    const stepDelay = (step) => new Promise(resolve => { setCurrentStep(step); setTimeout(resolve, 600); });

    try {
      await stepDelay(1);
      await stepDelay(2);

      let res;
      if (uploadFile) {
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('project_id', selectedProject);
        fd.append('scan_type', selectedScanType);
        await stepDelay(3);
        res = await axios.post(`${API}/scans/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await stepDelay(3);
        res = await axios.post(`${API}/scans`, {
          project_id: selectedProject, scan_type: selectedScanType,
          target: target || `https://${selectedScanType}-app.example.com`,
          scan_mode: scanMode,
          enable_spider: enableSpider || scanMode === 'spider',
          confirm_findings: confirmFindings || scanMode === 'aggressive',
          max_depth: maxDepth,
          product_id: selectedProduct && selectedProduct !== 'none' ? selectedProduct : '',
          engagement_id: selectedEngagement && selectedEngagement !== 'none' ? selectedEngagement : '',
        }, { withCredentials: true });
      }

      await stepDelay(4);
      await stepDelay(5);
      await stepDelay(6);

      setScanResult(res.data);
      fetchScans();
      checkAuth();
      toast.success(`Scan complete: ${res.data.total_findings} findings confirmed`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Scan failed';
      if (err.response?.status === 403) toast.error(detail, { action: { label: 'Upgrade', onClick: () => navigate('/pricing') } });
      else toast.error(detail);
    }
    finally { setScanning(false); setCurrentStep(0); }
  };

  return (
    <div className="space-y-6" data-testid="scanner-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="scanner-title">Vulnerability Scanner</h1>
          <p className="text-sm text-muted-foreground mt-1">Multi-phase security assessment pipeline{user?.credits && !user.credits.unlimited && <span className="ml-2 text-xs" data-testid="scanner-credits-hint">· {user.credits.credits_remaining} credits left ({Math.floor(user.credits.credits_remaining / (costs.scan || 10))} scans)</span>}</p>
        </div>
        <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-project-btn"><Plus className="h-4 w-4" /> New Project</Button></DialogTrigger>
          <DialogContent data-testid="new-project-dialog">
            <DialogHeader><DialogTitle>Create New Project</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div><Label>Project Name</Label><Input value={newProject.name} onChange={e => setNewProject(p => ({ ...p, name: e.target.value }))} placeholder="My Web Application" data-testid="project-name-input" /></div>
              <div><Label>Description</Label><Input value={newProject.description} onChange={e => setNewProject(p => ({ ...p, description: e.target.value }))} placeholder="Production web app" data-testid="project-desc-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Asset Type</Label><Select value={newProject.asset_type} onValueChange={v => setNewProject(p => ({ ...p, asset_type: v }))}><SelectTrigger data-testid="asset-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="web">Web</SelectItem><SelectItem value="api">API</SelectItem><SelectItem value="network">Network</SelectItem><SelectItem value="mobile">Mobile</SelectItem><SelectItem value="infrastructure">Infra</SelectItem></SelectContent></Select></div>
                <div><Label>Environment</Label><Select value={newProject.environment} onValueChange={v => setNewProject(p => ({ ...p, environment: v }))}><SelectTrigger data-testid="env-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="production">Production</SelectItem><SelectItem value="staging">Staging</SelectItem><SelectItem value="development">Dev</SelectItem></SelectContent></Select></div>
              </div>
              <Button onClick={createProject} className="w-full" disabled={!newProject.name} data-testid="create-project-btn">Create Project</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* ===== PIPELINE VISUALIZATION ===== */}
      <Card data-testid="pipeline-card">
        <CardContent className="p-6">
          <p className="text-sm font-medium mb-4">Scan Pipeline</p>
          <div className="flex items-center gap-0 overflow-x-auto pb-2">
            {pipelineSteps.map((ps, idx) => {
              const Icon = ps.icon;
              const isActive = scanning && currentStep === ps.step;
              const isDone = scanning ? currentStep > ps.step : (scanResult && true);
              const isWaiting = scanning && currentStep < ps.step;
              return (
                <div key={ps.step} className="flex items-center shrink-0">
                  <div className={`flex flex-col items-center gap-1.5 px-2 py-2 rounded-xl transition-all duration-300 min-w-[100px]
                    ${isActive ? 'bg-primary/10 border border-primary/40 shadow-[0_0_12px_rgba(59,130,246,0.2)]' : isDone ? 'bg-green-500/5 border border-green-500/20' : 'border border-transparent'}`}
                    data-testid={`pipeline-step-${ps.step}`}>
                    <div className={`p-2 rounded-lg transition-colors ${isActive ? 'bg-primary/20' : isDone ? 'bg-green-500/10' : 'bg-muted/50'}`}>
                      {isActive ? <Loader2 className="h-4 w-4 text-primary animate-spin" /> : isDone ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Icon className={`h-4 w-4 ${isWaiting ? 'text-muted-foreground/40' : 'text-muted-foreground'}`} />}
                    </div>
                    <p className={`text-[11px] font-medium text-center leading-tight ${isActive ? 'text-primary' : isDone ? 'text-green-500' : isWaiting ? 'text-muted-foreground/40' : 'text-muted-foreground'}`}>{ps.name}</p>
                  </div>
                  {idx < pipelineSteps.length - 1 && (
                    <div className={`w-6 h-px mx-0.5 shrink-0 ${isDone ? 'bg-green-500/40' : 'bg-border'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ===== SCAN CONFIG ===== */}
      <Card data-testid="scan-config-card">
        <CardContent className="p-6 space-y-5">
          {/* Target & Project Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2"><Label className="mb-2 block">Target URL</Label><Input value={target} onChange={e => setTarget(e.target.value)} placeholder="https://app.example.com" data-testid="target-input" /></div>
            <div><Label className="mb-2 block">Project</Label><Select value={selectedProject} onValueChange={setSelectedProject}><SelectTrigger data-testid="project-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex items-end"><Button onClick={startScan} disabled={scanning || !selectedProject} className="w-full gap-2 h-10" data-testid="start-scan-btn">{scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}{scanning ? 'Scanning...' : 'Start Scan'}<Badge variant="outline" className="ml-1 gap-1 border-white/30 text-white/90 bg-white/10 text-[10px] px-1.5" data-testid="scan-cost-badge"><Coins className="h-3 w-3" /> {costs.scan} cr</Badge></Button></div>
          </div>

          {/* Product & Engagement */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label className="mb-2 block">Product (optional)</Label><Select value={selectedProduct} onValueChange={setSelectedProduct}><SelectTrigger data-testid="product-select"><SelectValue placeholder="Link to product" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-2 block">Engagement (optional)</Label><Select value={selectedEngagement} onValueChange={setSelectedEngagement} disabled={!selectedProduct || selectedProduct === 'none'}><SelectTrigger data-testid="engagement-select"><SelectValue placeholder={selectedProduct && selectedProduct !== 'none' ? 'Select' : 'Select product first'} /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{engagements.map(e => <SelectItem key={e.engagement_id} value={e.engagement_id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-2 block">Spider Depth</Label><Input type="number" min="1" max="10" value={maxDepth} onChange={e => setMaxDepth(parseInt(e.target.value) || 3)} data-testid="depth-input" /></div>
          </div>

          <Separator />

          {/* Scan Mode */}
          <div>
            <Label className="mb-3 block">Scan Mode</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {scanModes.map(mode => {
                const MI = mode.icon;
                return (
                  <button key={mode.value} onClick={() => setScanMode(mode.value)}
                    className={`p-3.5 rounded-xl border text-left transition-all duration-200 ${scanMode === mode.value ? 'border-primary bg-primary/5 shadow-[0_0_10px_rgba(59,130,246,0.15)]' : 'border-border hover:border-primary/30'}`}
                    data-testid={`scan-mode-${mode.value}`}>
                    <MI className={`h-5 w-5 mb-1.5 ${scanMode === mode.value ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="text-sm font-medium">{mode.label}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{mode.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Advanced Options */}
          <div className="p-4 rounded-xl border bg-muted/30">
            <p className="text-sm font-medium mb-3 flex items-center gap-2"><Crosshair className="h-4 w-4 text-primary" /> Advanced Options</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div><p className="text-sm font-medium">Spider Crawl</p><p className="text-xs text-muted-foreground">Discover hidden endpoints</p></div>
                <Switch checked={enableSpider || scanMode === 'spider'} onCheckedChange={setEnableSpider} disabled={scanMode === 'spider'} data-testid="spider-toggle" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border bg-card">
                <div><p className="text-sm font-medium">Confirm & Validate</p><p className="text-xs text-muted-foreground">Scripts to verify, remove false positives</p></div>
                <Switch checked={confirmFindings || scanMode === 'aggressive'} onCheckedChange={setConfirmFindings} disabled={scanMode === 'aggressive'} data-testid="confirm-toggle" />
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="p-4 rounded-xl border border-dashed border-border hover:border-primary/40 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10"><Upload className="h-5 w-5 text-primary" /></div>
              <div className="flex-1"><p className="text-sm font-medium">Upload File for Scanning</p><p className="text-xs text-muted-foreground">APK, IPA, Dockerfile, YAML, PCAP</p></div>
              <label className="cursor-pointer"><input type="file" className="hidden" onChange={e => setUploadFile(e.target.files?.[0] || null)} data-testid="file-upload-input" /><Button variant="outline" size="sm" asChild><span><Upload className="h-3.5 w-3.5 mr-1" /> Browse</span></Button></label>
            </div>
            {uploadFile && (<div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-muted/50"><FileType className="h-4 w-4 text-primary shrink-0" /><span className="text-sm font-mono truncate flex-1">{uploadFile.name}</span><Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setUploadFile(null)} data-testid="remove-file-btn">Remove</Button></div>)}
          </div>

          {/* Scan Types */}
          <div>
            <Label className="mb-3 block">Scan Type</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(scanTypes.length > 0 ? scanTypes : [
                { id: 'web', label: 'Web App', desc: 'OWASP Top 10' }, { id: 'api', label: 'API', desc: 'BOLA, Auth' },
                { id: 'network', label: 'Network', desc: 'Ports, Services' }, { id: 'infrastructure', label: 'Infra', desc: 'IAM, Storage' },
                { id: 'container', label: 'Container', desc: 'Image Vulns' }, { id: 'kubernetes', label: 'K8s', desc: 'RBAC, Pods' },
                { id: 'mobile', label: 'Mobile', desc: 'APK/IPA' }, { id: 'iot', label: 'IoT', desc: 'Firmware' },
              ]).map(type => {
                const Icon = scanTypeIcons[type.id] || Shield;
                return (
                  <button key={type.id} onClick={() => setSelectedScanType(type.id)}
                    className={`p-3 rounded-xl border text-left transition-all ${selectedScanType === type.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`}
                    data-testid={`scan-type-${type.id}`}>
                    <Icon className={`h-4 w-4 mb-1 ${selectedScanType === type.id ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="text-sm font-medium">{type.label}</p>
                    <p className="text-[11px] text-muted-foreground">{type.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== SCAN RESULT WITH PIPELINE DETAILS ===== */}
      {scanResult && (
        <Card className="border-green-500/30" data-testid="scan-result-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><CheckCircle className="h-5 w-5 text-green-500" /> Scan Complete — {scanResult.total_findings} Findings</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {/* Severity Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="text-center p-3 rounded-lg bg-red-500/10"><p className="text-xl font-bold text-red-500">{scanResult.critical_count}</p><p className="text-[11px] text-muted-foreground">Critical</p></div>
              <div className="text-center p-3 rounded-lg bg-orange-500/10"><p className="text-xl font-bold text-orange-500">{scanResult.high_count}</p><p className="text-[11px] text-muted-foreground">High</p></div>
              <div className="text-center p-3 rounded-lg bg-yellow-500/10"><p className="text-xl font-bold text-yellow-500">{scanResult.medium_count}</p><p className="text-[11px] text-muted-foreground">Medium</p></div>
              <div className="text-center p-3 rounded-lg bg-green-500/10"><p className="text-xl font-bold text-green-500">{scanResult.low_count}</p><p className="text-[11px] text-muted-foreground">Low</p></div>
              <div className="text-center p-3 rounded-lg bg-blue-500/10"><p className="text-xl font-bold text-blue-500">{scanResult.informational_count}</p><p className="text-[11px] text-muted-foreground">Info</p></div>
            </div>

            {/* Badges */}
            <div className="flex gap-2 flex-wrap">
              {scanResult.false_positives_removed > 0 && <Badge variant="outline" className="border-green-500/30 text-green-500 bg-green-500/10 gap-1" data-testid="fp-removed"><ShieldCheck className="h-3 w-3" /> {scanResult.false_positives_removed} false positives removed</Badge>}
              {scanResult.spider_discovered > 0 && <Badge variant="outline" className="border-purple-500/30 text-purple-500 bg-purple-500/10 gap-1" data-testid="spider-found"><Radar className="h-3 w-3" /> {scanResult.spider_discovered} spider discovered</Badge>}
              {scanResult.confirmed_findings > 0 && <Badge variant="outline" className="border-blue-500/30 text-blue-500 bg-blue-500/10 gap-1" data-testid="confirmed-count"><CheckCircle className="h-3 w-3" /> {scanResult.confirmed_findings} confirmed</Badge>}
            </div>

            {/* Pipeline Step Details */}
            {scanResult.pipeline && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Pipeline Details</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {scanResult.pipeline.map(step => (
                    <div key={step.step} className="p-3 rounded-lg border bg-card" data-testid={`result-step-${step.step}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        <p className="text-xs font-medium">{step.name}</p>
                      </div>
                      <div className="space-y-0.5">
                        {Object.entries(step.details || {}).map(([k, v]) => (
                          <div key={k} className="flex justify-between text-[11px]">
                            <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
                            <span className="font-mono">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate('/vulnerabilities')} data-testid="view-findings-btn"><Bug className="h-3.5 w-3.5" /> View All Findings</Button>
          </CardContent>
        </Card>
      )}

      {/* ===== SCAN HISTORY ===== */}
      <Card data-testid="scan-history-card">
        <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-base font-heading">Scan History</CardTitle><Button variant="ghost" size="sm" onClick={fetchScans} data-testid="refresh-scans"><RefreshCw className="h-4 w-4" /></Button></div></CardHeader>
        <CardContent><ScrollArea className="h-[300px]"><div className="space-y-2">
          {scans.map(scan => (
            <div key={scan.scan_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`scan-${scan.scan_id}`}>
              <Shield className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{scan.scan_type?.toUpperCase()} {scan.scan_mode && scan.scan_mode !== 'standard' ? `(${scan.scan_mode})` : ''}</p>
                <p className="text-[11px] text-muted-foreground font-mono truncate">{scan.target || 'N/A'}</p>
              </div>
              <Badge variant="outline" className={scan.status === 'completed' ? 'border-green-500/30 text-green-500' : 'border-blue-500/30 text-blue-500'}>{scan.status}</Badge>
              <span className="text-xs text-muted-foreground">{scan.total_findings} findings</span>
              {scan.false_positives_removed > 0 && <Badge variant="outline" className="text-[10px] border-green-500/20 text-green-500">-{scan.false_positives_removed} FP</Badge>}
              <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(scan.started_at).toLocaleDateString()}</span>
            </div>
          ))}
          {scans.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No scans yet. Run your first scan above.</div>}
        </div></ScrollArea></CardContent>
      </Card>
    </div>
  );
}
